import { useToast } from '@/hooks/useToast';
import { projectContactsService } from '@/services/projectContactsService';
import { saveAs } from 'file-saver';
import { useCallback, useRef, useState } from 'react';
import * as XLSX from 'xlsx';

/**
 * Import Contacts Modal Component
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether modal is open
 * @param {Function} props.onClose - Close modal handler
 * @param {string} props.projectId - Project ID
 * @param {Function} props.onImportSuccess - Success callback
 * @returns {JSX.Element} ImportContactsModal component
 */
const ImportContactsModal = ({
  isOpen,
  onClose,
  projectId,
  onImportSuccess,
}) => {
  const fileInputRef = useRef(null);
  const { showSuccess, showError } = useToast();

  // State management
  const [isLoading, setIsLoading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [previewData, setPreviewData] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [actionOnDuplicate, setActionOnDuplicate] = useState('skip'); // 'skip' or 'replace'

  // Reset state when modal closes
  const handleClose = useCallback(() => {
    setUploadedFile(null);
    setPreviewData([]);
    setIsProcessing(false);
    setDragActive(false);
    setActionOnDuplicate('skip');
    onClose();
  }, [onClose]);

  // Parse CSV data
  const parseCSV = csvText => {
    const lines = csvText.split('\n').filter(line => line.trim());
    if (lines.length < 2) return [];

    // Get headers from first line
    const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));

    // Find column indices for our expected fields
    const nameIndex = headers.findIndex(h => h.toLowerCase() === 'name');
    const phoneIndex = headers.findIndex(
      h => h.toLowerCase() === 'phonenumber'
    );
    const dobIndex = headers.findIndex(h => h.toLowerCase() === 'dateofbirth');
    const tagsIndex = headers.findIndex(h => h.toLowerCase() === 'tags');
    const notesIndex = headers.findIndex(h => h.toLowerCase() === 'notes');

    if (nameIndex === -1 || phoneIndex === -1) {
      throw new Error(
        'Required columns "name" and "phoneNumber" not found in file'
      );
    }

    // Parse data rows
    const data = [];
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim().replace(/"/g, ''));

      if (values.length >= Math.max(nameIndex, phoneIndex) + 1) {
        // Parse tags if present
        let tags = [];
        if (tagsIndex !== -1 && values[tagsIndex]) {
          const tagsStr = values[tagsIndex].trim();
          if (tagsStr) {
            tags = tagsStr
              .split(',')
              .map(tag => tag.trim())
              .filter(tag => tag);
          }
        }

        data.push({
          name: values[nameIndex] || '',
          phoneNumber: values[phoneIndex] || '',
          dateOfBirth: dobIndex !== -1 ? values[dobIndex] || '' : '',
          tags: tags,
          notes: notesIndex !== -1 ? values[notesIndex] || '' : '',
        });
      }
    }

    return data;
  };

  // Parse Excel data
  const parseExcel = arrayBuffer => {
    try {
      // Read the Excel file
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });

      // Get the first worksheet
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];

      // Convert to JSON
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

      if (jsonData.length < 2) return [];

      // Get headers from first row
      const headers = jsonData[0].map(h => String(h).trim().toLowerCase());

      // Find column indices for our expected fields
      const nameIndex = headers.findIndex(h => h === 'name');
      const phoneIndex = headers.findIndex(h => h === 'phonenumber');
      const dobIndex = headers.findIndex(h => h === 'dateofbirth');
      const tagsIndex = headers.findIndex(h => h === 'tags');
      const notesIndex = headers.findIndex(h => h === 'notes');

      if (nameIndex === -1 || phoneIndex === -1) {
        throw new Error(
          'Required columns "name" and "phoneNumber" not found in file'
        );
      }

      // Parse data rows
      const data = [];
      for (let i = 1; i < jsonData.length; i++) {
        const row = jsonData[i];

        if (row && row.length >= Math.max(nameIndex, phoneIndex) + 1) {
          // Handle date parsing for Excel
          let dateOfBirth = '';
          if (dobIndex !== -1 && row[dobIndex]) {
            const dobValue = row[dobIndex];
            if (typeof dobValue === 'number') {
              // Excel date serial number - convert to date
              const excelDate = XLSX.SSF.parse_date_code(dobValue);
              if (excelDate) {
                dateOfBirth = `${excelDate.y}-${String(excelDate.m).padStart(2, '0')}-${String(excelDate.d).padStart(2, '0')}`;
              }
            } else {
              // String date - try to parse and format
              const dateStr = String(dobValue).trim();
              if (dateStr) {
                // Check if it's already in YYYY-MM-DD format
                if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
                  dateOfBirth = dateStr;
                } else {
                  // Try to parse other date formats
                  const parsedDate = new Date(dateStr);
                  if (!isNaN(parsedDate.getTime())) {
                    dateOfBirth = parsedDate.toISOString().split('T')[0];
                  } else {
                    dateOfBirth = dateStr; // Keep original if can't parse
                  }
                }
              }
            }
          }

          // Parse tags if present
          let tags = [];
          if (tagsIndex !== -1 && row[tagsIndex]) {
            const tagsStr = String(row[tagsIndex]).trim();
            if (tagsStr) {
              tags = tagsStr
                .split(',')
                .map(tag => tag.trim())
                .filter(tag => tag);
            }
          }

          data.push({
            name: String(row[nameIndex] || '').trim(),
            phoneNumber: String(row[phoneIndex] || '').trim(),
            dateOfBirth: dateOfBirth,
            tags: tags,
            notes:
              notesIndex !== -1 ? String(row[notesIndex] || '').trim() : '',
          });
        }
      }

      return data;
    } catch (error) {
      throw new Error(`Error parsing Excel file: ${error.message}`);
    }
  };

  // Validate contact data
  const validateContactData = contact => {
    const errors = [];

    if (!contact.name || contact.name.trim() === '') {
      errors.push('Name is required');
    }

    if (!contact.phoneNumber || contact.phoneNumber.trim() === '') {
      errors.push('Phone number is required');
    } else if (!/^\+?[\d\s\-()]+$/.test(contact.phoneNumber)) {
      errors.push('Invalid phone number format');
    }

    if (contact.dateOfBirth && contact.dateOfBirth.trim() !== '') {
      const dobStr = contact.dateOfBirth.trim();
      // Check if it's in YYYY-MM-DD format
      if (!/^\d{4}-\d{2}-\d{2}$/.test(dobStr)) {
        // Try to parse other date formats
        const dob = new Date(dobStr);
        if (isNaN(dob.getTime())) {
          errors.push('Invalid date of birth format (expected YYYY-MM-DD)');
        }
      }
    }

    return errors;
  };

  // Parse file for preview
  const parseFileForPreview = useCallback(async file => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = e => {
        try {
          const data = e.target.result;
          let parsedData = [];

          if (file.type === 'text/csv') {
            // Parse CSV
            parsedData = parseCSV(data);
          } else if (
            file.type ===
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
            file.type === 'application/vnd.ms-excel'
          ) {
            // Parse Excel files
            parsedData = parseExcel(data);
          } else {
            reject(
              new Error(
                'Unsupported file format. Please use CSV or Excel files.'
              )
            );
            return;
          }

          // Validate and add errors
          const validatedData = parsedData.map((row, index) => ({
            id: index + 1,
            ...row,
            errors: validateContactData(row),
          }));

          resolve(validatedData);
        } catch (error) {
          reject(error);
        }
      };

      reader.onerror = () => reject(new Error('Failed to read file'));

      // Use ArrayBuffer for Excel files, text for CSV
      if (file.type === 'text/csv') {
        reader.readAsText(file);
      } else {
        reader.readAsArrayBuffer(file);
      }
    });
  }, []);

  // Handle file selection
  const handleFileSelect = useCallback(
    async file => {
      if (!file) return;

      // Validate file type
      const allowedTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
        'application/vnd.ms-excel', // .xls
        'text/csv', // .csv
      ];

      if (!allowedTypes.includes(file.type)) {
        showError('Please select a valid Excel (.xlsx, .xls) or CSV file.');
        return;
      }

      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        showError('File size must be less than 10MB.');
        return;
      }

      setUploadedFile(file);
      setIsProcessing(true);

      try {
        // Parse the file to show preview
        const parsedData = await parseFileForPreview(file);
        setPreviewData(parsedData);
      } catch (error) {
        console.error('Error parsing file:', error);
        showError('Error parsing file. Please check the file format.');
        setUploadedFile(null);
      } finally {
        setIsProcessing(false);
      }
    },
    [showError, parseFileForPreview]
  );

  // Handle file input change
  const handleFileInputChange = event => {
    const file = event.target.files[0];
    handleFileSelect(file);
  };

  // Handle drag and drop
  const handleDrag = useCallback(e => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback(
    e => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);

      if (e.dataTransfer.files && e.dataTransfer.files[0]) {
        handleFileSelect(e.dataTransfer.files[0]);
      }
    },
    [handleFileSelect]
  );

  // Download sample format
  const handleDownloadSample = async () => {
    try {
      const blob = await projectContactsService.getContactImportTemplate();
      saveAs(blob, 'contacts-import-template.xlsx');
      showSuccess('Template downloaded successfully!');
    } catch (error) {
      console.error('Error downloading template:', error);
      showError('Error downloading template. Please try again.');
    }
  };

  // Remove row from preview
  const handleRemoveRow = rowId => {
    setPreviewData(prev => prev.filter(row => row.id !== rowId));
  };

  // Process import
  const handleImport = async () => {
    if (!uploadedFile || previewData.length === 0) {
      showError('Please upload a file first.');
      return;
    }

    // Check for errors in preview data
    const hasErrors = previewData.some(
      row => row.errors && row.errors.length > 0
    );
    if (hasErrors) {
      showError('Please fix all errors before importing.');
      return;
    }

    setIsLoading(true);

    try {
      const response = await projectContactsService.importProjectContacts(
        projectId,
        previewData,
        {
          actionOnDuplicate: actionOnDuplicate,
        }
      );

      const summary = response.data?.summary;
      if (summary) {
        showSuccess(
          `Bulk upload completed. Created: ${summary.created}, Updated: ${summary.updated}, Skipped: ${summary.skipped}`
        );
      } else {
        showSuccess(`Successfully imported ${previewData.length} contacts!`);
      }

      if (onImportSuccess) {
        onImportSuccess();
      }

      handleClose();
    } catch (error) {
      console.error('Import error:', error);

      // Handle authentication errors specifically
      if (error.message && error.message.includes('Authentication failed')) {
        showError('Your session has expired. Please login again.');
        // Trigger logout after a short delay
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('auth-state-changed'));
        }, 2000);
      } else {
        showError(
          'Error importing contacts. Please check the file format and try again.'
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen p-4 text-center">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={handleClose}
        ></div>

        {/* Modal */}
        <div className="relative inline-block align-middle bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all w-full max-w-7xl">
          {/* Header */}
          <div className="bg-white px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium text-gray-900">
                  Import Contacts
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  Upload an Excel or CSV file to import contacts
                </p>
              </div>
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-600 focus:outline-none focus:text-gray-600"
              >
                <svg
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="bg-white px-6 py-4 max-h-[70vh] overflow-y-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left Column - Upload & Instructions */}
              <div className="space-y-6">
                {/* Instructions */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-blue-900 mb-2">
                    Import Instructions
                  </h4>
                  <ul className="text-sm text-blue-800 space-y-1">
                    <li>• Supported formats: Excel (.xlsx, .xls) and CSV</li>
                    <li>• Maximum file size: 10MB</li>
                    <li>• Required columns: name, phoneNumber</li>
                    <li>
                      • Optional columns: dateOfBirth (YYYY-MM-DD), tags
                      (comma-separated), notes
                    </li>
                    <li>
                      • Tags that don't exist will be created automatically
                    </li>
                    <li>
                      • Download the sample template for proper formatting
                    </li>
                  </ul>
                </div>

                {/* File Upload Area */}
                <div
                  className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                    dragActive
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                >
                  {isProcessing ? (
                    <div className="flex flex-col items-center">
                      <svg
                        className="animate-spin h-8 w-8 text-primary-600 mb-2"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      <p className="text-sm text-gray-600">
                        Processing file...
                      </p>
                    </div>
                  ) : uploadedFile ? (
                    <div className="flex flex-col items-center">
                      <svg
                        className="h-8 w-8 text-green-600 mb-2"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <p className="text-sm font-medium text-gray-900 mb-1">
                        {uploadedFile.name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {(uploadedFile.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                      <button
                        onClick={() => {
                          setUploadedFile(null);
                          setPreviewData([]);
                          if (fileInputRef.current) {
                            fileInputRef.current.value = '';
                          }
                        }}
                        className="mt-2 text-xs text-red-600 hover:text-red-800"
                      >
                        Remove file
                      </button>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center">
                      <svg
                        className="h-8 w-8 text-gray-400 mb-2"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                        />
                      </svg>
                      <p className="text-sm text-gray-600 mb-2">
                        Drag and drop your file here, or
                      </p>
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                      >
                        Browse Files
                      </button>
                    </div>
                  )}
                </div>

                {/* Hidden file input */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={handleFileInputChange}
                  className="hidden"
                />

                {/* Download Template Button */}
                <div className="text-center">
                  <button
                    onClick={handleDownloadSample}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    <svg
                      className="h-4 w-4 mr-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                    Download Sample Template
                  </button>
                </div>
              </div>

              {/* Right Column - Data Preview */}
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-3">
                  Data Preview ({previewData.length} rows)
                </h4>

                {previewData.length > 0 ? (
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="max-h-80 overflow-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50 sticky top-0">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                              Name
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                              Phone Number
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                              Date of Birth
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                              Tags
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                              Notes
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Status
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {previewData.map(row => (
                            <tr
                              key={row.id}
                              className={
                                row.errors?.length > 0 ? 'bg-red-50' : ''
                              }
                            >
                              <td className="px-3 py-2 text-sm text-gray-900 whitespace-nowrap">
                                {row.name}
                              </td>
                              <td className="px-3 py-2 text-sm text-gray-900 whitespace-nowrap">
                                {row.phoneNumber}
                              </td>
                              <td className="px-3 py-2 text-sm text-gray-900 whitespace-nowrap">
                                {row.dateOfBirth || '-'}
                              </td>
                              <td className="px-3 py-2 text-sm text-gray-900 whitespace-nowrap">
                                {row.tags && row.tags.length > 0
                                  ? row.tags.join(', ')
                                  : '-'}
                              </td>
                              <td className="px-3 py-2 text-sm text-gray-900 whitespace-nowrap">
                                {row.notes || '-'}
                              </td>
                              <td className="px-3 py-2 text-sm">
                                {row.errors?.length > 0 ? (
                                  <div className="max-w-xs">
                                    <ul className="text-xs text-red-700 space-y-1">
                                      {row.errors.map((error, index) => (
                                        <li
                                          key={index}
                                          className="flex items-start"
                                        >
                                          <span className="text-red-500 mr-1">
                                            •
                                          </span>
                                          <span className="leading-tight">
                                            {error}
                                          </span>
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                ) : (
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    <svg
                                      className="h-3 w-3 mr-1"
                                      fill="currentColor"
                                      viewBox="0 0 20 20"
                                    >
                                      <path
                                        fillRule="evenodd"
                                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                        clipRule="evenodd"
                                      />
                                    </svg>
                                    Valid
                                  </span>
                                )}
                              </td>
                              <td className="px-3 py-2 text-sm whitespace-nowrap">
                                <button
                                  onClick={() => handleRemoveRow(row.id)}
                                  className="text-red-600 hover:text-red-800"
                                >
                                  Remove
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <div className="border border-gray-200 rounded-lg p-8 text-center">
                    <svg
                      className="h-8 w-8 text-gray-400 mx-auto mb-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                    <p className="text-sm text-gray-500">
                      Upload a file to see data preview
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Duplicate Handling Options - Only show when data is loaded */}
            {previewData.length > 0 && (
              <div className="mt-6 border-t border-gray-200 pt-6">
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-yellow-900 mb-3">
                    Duplicate Handling
                  </h4>
                  <p className="text-sm text-yellow-800 mb-4">
                    Choose how to handle contacts that already exist in the
                    database:
                  </p>
                  <div className="space-y-3">
                    <label className="flex items-start">
                      <input
                        type="radio"
                        name="actionOnDuplicate"
                        value="skip"
                        checked={actionOnDuplicate === 'skip'}
                        onChange={e => setActionOnDuplicate(e.target.value)}
                        className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                      />
                      <div className="ml-3">
                        <div className="text-sm font-medium text-yellow-900">
                          Skip duplicates
                        </div>
                        <div className="text-sm text-yellow-700">
                          Keep existing contacts unchanged, only import new ones
                        </div>
                      </div>
                    </label>
                    <label className="flex items-start">
                      <input
                        type="radio"
                        name="actionOnDuplicate"
                        value="replace"
                        checked={actionOnDuplicate === 'replace'}
                        onChange={e => setActionOnDuplicate(e.target.value)}
                        className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                      />
                      <div className="ml-3">
                        <div className="text-sm font-medium text-yellow-900">
                          Replace duplicates
                        </div>
                        <div className="text-sm text-yellow-700">
                          Update existing contacts with new data from the file
                        </div>
                      </div>
                    </label>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-3 flex justify-end space-x-3">
            <button
              onClick={handleClose}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              Cancel
            </button>
            <button
              onClick={handleImport}
              disabled={!uploadedFile || previewData.length === 0 || isLoading}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <div className="flex items-center">
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Importing...
                </div>
              ) : (
                `Import ${previewData.length} Contacts`
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImportContactsModal;
